import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { useUserState } from "../Contexts/UserContext";
import LoadingButton from "./common/LoadingButton";
import "./MonthlyPayment.css";

const MonthlyPayment = () => {
    const { id } = useParams();
    const baseUrl = import.meta.env.VITE_APP_BASE_URL;

    const { user } = useUserState();

    const [schedule, setSchedule] = useState(null);
    const [cards, setCards] = useState([]);
    const [selectedCard, setSelectedCard] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    const headers = {
        Authorization: user?.token,
    };

    // 🔹 Fetch schedule
    const fetchSchedule = async () => {
        try {
            const res = await axios.get(
                `${baseUrl}/api/payment-method/get-schedule-detail/${id}`,
                { headers }
            );
            setSchedule(res.data.data);
        } catch (error) {
            console.error("Error fetching schedule:", error);
            setErr("Failed to load payment details.");
        }
    };

    // 🔹 Fetch cards
    const fetchCards = async () => {
        try {
            const res = await axios.get(
                `${baseUrl}/api/payment-method/get-list`,
                { headers }
            );
            setCards(res?.data?.data?.data || []);
            // Auto-select first card if available
            if (res?.data?.data?.data?.length > 0) {
                setSelectedCard(res.data.data.data[0]);
            }
        } catch (error) {
            console.error("Error fetching cards:", error);
        }
    };

    useEffect(() => {
        fetchSchedule();
        fetchCards();
    }, []);

    // PAY FUNCTION
    const handlePay = async () => {
        if (!selectedCard) {
            setErr("Please select a payment method");
            return;
        }

        try {
            setLoading(true);
            setErr("");

            const res = await axios.post(
                `${baseUrl}/api/payment-method/pay-monthly-installment`,
                {
                    schedule_id: id,
                    paymentMethod: selectedCard.id,
                },
                { headers }
            );

            alert("Payment successful!");
            fetchSchedule(); // refresh UI
        } catch (error) {
            console.error(error);
            const msg = error?.response?.data?.message || "Payment failed. Please try again.";
            setErr(msg);
        } finally {
            setLoading(false);
        }
    };

    if (!schedule && !err) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    const isPaid = schedule?.status?.toLowerCase() === "paid";

    return (
        <div className="container">
            <div className="monthly-payment-container">
                <div className="payment-header">
                    <h2>Monthly Rent Payment</h2>
                    <p>Complete your rent payment securely using your saved cards.</p>
                </div>

                {/* 🔹 PAYMENT INFO SUMMARY */}
                {schedule && (
                    <div className="summary-card">
                        <div className="summary-item">
                            <span className="summary-label">Month</span>
                            <span className="summary-value">{schedule.month}</span>
                        </div>
                        <div className="summary-item">
                            <span className="summary-label">Amount</span>
                            <span className="summary-value" style={{ fontSize: '1.4rem', color: '#2080df' }}>
                                ${schedule.amount}
                            </span>
                        </div>
                        <div className="summary-item">
                            <span className="summary-label">Due Date</span>
                            <span className="summary-value">
                                {new Date(schedule.due_date).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric'
                                })}
                            </span>
                        </div>
                        <div className="summary-item">
                            <span className="summary-label">Status</span>
                            <span className={`summary-value status-${schedule.status?.toLowerCase()}`}>
                                <i className={`fa-solid ${isPaid ? 'fa-circle-check' : 'fa-circle-exclamation'} me-2`}></i>
                                {schedule.status}
                            </span>
                        </div>
                    </div>
                )}

                {/* 🔹 CARD LIST */}
                {!isPaid && (
                    <>
                        <h4 className="card-selection-title">Select Payment Method</h4>

                        {cards.length > 0 ? (
                            <div className="cards-grid">
                                {cards.map((card, i) => (
                                    <div
                                        key={i}
                                        className={`payment-card-option ${selectedCard?.id === card.id ? 'selected' : ''}`}
                                        onClick={() => setSelectedCard(card)}
                                    >
                                        <input
                                            type="radio"
                                            name="card"
                                            className="card-radio-hidden"
                                            checked={selectedCard?.id === card.id}
                                            readOnly
                                        />
                                        <div className="card-icon-wrapper">
                                            <i className={`fa-brands fa-cc-${card.card.brand || 'visa'}`}></i>
                                        </div>
                                        <div className="card-details">
                                            <span className="card-name">{card.billing_details.name || 'Saved Card'}</span>
                                            <span className="card-number">•••• •••• •••• {card.card.last4}</span>
                                        </div>
                                        <div className="selection-indicator"></div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="alert alert-info">
                                No saved cards found. Please add a card in your billing settings.
                            </div>
                        )}

                        {/* 🔹 ERROR DISPLAY */}
                        {err && (
                            <div className="error-container">
                                <i className="fa-solid fa-triangle-exclamation"></i>
                                <span>{err}</span>
                            </div>
                        )}

                        {/* 🔹 PAY BUTTON */}
                        <div className="pay-action-container">
                            <LoadingButton
                                className="button pay-now-btn"
                                onClick={handlePay}
                                isLoading={loading}
                                disabled={loading || cards.length === 0}
                            >
                                <i className="fa-solid fa-lock me-2"></i>
                                Pay ${schedule?.amount} Now
                            </LoadingButton>
                        </div>
                    </>
                )}

                {isPaid && (
                    <div className="text-center py-5">
                        <div className="mb-4" style={{ fontSize: '4rem', color: '#28a745' }}>
                            <i className="fa-solid fa-circle-check"></i>
                        </div>
                        <h3>Payment Completed</h3>
                        <p className="text-muted">This installment has already been paid successfully. Thank you!</p>
                        <button
                            className="btn btn-outline-primary mt-3"
                            onClick={() => window.history.back()}
                        >
                            Go Back
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MonthlyPayment;